import { useEffect, useRef, useState } from "react";
import type { MaterialKind } from "../../../shared/api-contract";
import { optionFromLibrary } from "../../../features/vault/applyOption";
import { Icon } from "../../kit";
import {
  CATEGORY_ORDER,
  CATEGORY_PANEL_LABEL,
  MaterialCategoryGrid,
  type Category,
  type LibMaterial,
} from "../library/MaterialCategoryGrid";
import { crossesKind } from "./optionMark";
import { VIEWER_ORIGIN } from "./useConfiguratorEditing";

/**
 * The "+" picker: the studio's material library, by category, where picking a
 * material makes it a customer choice for one part. Shared by the canvas bar
 * (in a right-side panel) and the configurator drawer (inline), so both offer
 * exactly the same materials.
 *
 * The grid is the library's own (`MaterialCategoryGrid` in `target` mode): the
 * host decides what a click does, so nothing here touches the viewer.
 *
 * All five tabs, as iJewel shows them — the part's OWN categories first and
 * open, the other kind's on the row below. The part's kind is the slot's
 * stable one (`slotKindOf`), never what it happens to be wearing: a stone slot
 * opens on Gems even while the stones are showing as metal.
 *
 * A material that would turn the part into the other kind (a metal or a pearl
 * on the stones, a diamond on the band) is not added straight away: the
 * picker asks first, inline — that one click is how "Gem 01" became metal.
 * Mount it keyed by slot, so switching parts starts on the new part's tab.
 */

/** What a part sensibly becomes: its own kind's categories, and "other". */
export const SLOT_CATEGORIES: Record<MaterialKind, Category[]> = {
  metal: ["metal", "ceramic", "other"],
  gem: ["gem", "pearl", "other"],
};

/** Every tab, the part's own first. */
export function pickerTabs(kind: MaterialKind): { own: Category[]; rest: Category[] } {
  const own = SLOT_CATEGORIES[kind];
  return { own, rest: CATEGORY_ORDER.filter((c) => !own.includes(c)) };
}

/** The left rail's icon for each category, so the tabs read as the same places. */
const CATEGORY_ICON: Record<Category, string> = {
  metal: "circle",
  gem: "diamond",
  ceramic: "contrast",
  pearl: "bubble_chart",
  other: "grain",
};

/** The viewer's stone ids, in a jeweller's words (as Basic's Gems panel says them). */
const GEM_WORDS: Record<string, string> = { diamondClear: "Diamond", diamondWarm: "Warm diamond" };
/** Indistinguishable from `diamondClear` on screen (measured; see MaterialPanels). */
const GEM_HIDDEN = ["diamondClear2"];

/** The question, in the words of what will actually happen to the part. */
function crossingWords(item: LibMaterial, kind: MaterialKind): { title: string; detail: string } {
  if (kind === "gem") {
    const what =
      item.category === "metal"
        ? "is a metal"
        : item.category === "ceramic"
          ? "is a ceramic"
          : item.category === "pearl"
            ? "is drawn as a solid surface"
            : "is a solid surface";
    return {
      title: "This turns the stones into metal. Add anyway?",
      detail: `${item.label} ${what}, so these stones would stop sparkling.`,
    };
  }
  return {
    title: "This turns this metal part into a stone. Add anyway?",
    detail: `${item.label} is cut-stone material, so this part would render as a stone.`,
  };
}

export function OptionPicker({
  kind,
  onPick,
  activeId,
}: {
  /** The slot's kind (`slotKindOf`) — which categories come first. */
  kind: MaterialKind;
  /** Add the picked material as a choice (and show it). */
  onPick: (item: LibMaterial) => Promise<unknown>;
  /** The library item to ring as chosen. */
  activeId?: string | null;
}) {
  const { own, rest } = pickerTabs(kind);
  const [tab, setTab] = useState<Category>(own[0]);
  /** A material that would change the part's kind, waiting for a yes. */
  const [pending, setPending] = useState<LibMaterial | null>(null);
  const [adding, setAdding] = useState(false);
  const confirmRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (pending) confirmRef.current?.querySelector<HTMLButtonElement>("[data-config-cross-cancel]")?.focus();
  }, [pending]);

  const pick = (item: LibMaterial): Promise<unknown> => {
    const candidate = optionFromLibrary(item, { viewer: VIEWER_ORIGIN, page: window.location.origin });
    if (crossesKind(candidate, kind)) {
      setPending(item);
      return Promise.resolve();
    }
    setPending(null);
    return onPick(item);
  };

  const confirm = async () => {
    if (!pending || adding) return;
    setAdding(true);
    try {
      await onPick(pending);
    } finally {
      setAdding(false);
      setPending(null);
    }
  };

  const tabButton = (c: Category) => (
    <button
      key={c}
      type="button"
      role="tab"
      aria-selected={tab === c}
      onClick={() => setTab(c)}
      className={`inline-flex h-[30px] items-center gap-1.5 rounded-lg border px-2.5 text-[12px] transition-colors ${
        tab === c
          ? "border-ed-gold bg-ed-active font-semibold text-ed-text"
          : "border-ed-border font-medium text-ed-text-2 hover:border-ed-line hover:text-ed-text"
      }`}
      data-config-tab={c}
    >
      <Icon name={CATEGORY_ICON[c]} size={16} />
      {CATEGORY_PANEL_LABEL[c]}
    </button>
  );

  const words = pending ? crossingWords(pending, kind) : null;

  return (
    <div className="flex flex-col gap-3" data-config-picker-body={kind}>
      <div role="tablist" aria-label="Material type" className="flex flex-wrap items-center gap-1.5">
        {own.map(tabButton)}
        {/* The other kind's categories on a row of their own. */}
        <span className="basis-full" aria-hidden />
        {rest.map(tabButton)}
      </div>
      {pending && words ? (
        <div
          ref={confirmRef}
          role="group"
          aria-label="Change what this part is?"
          className="sticky top-0 z-10 rounded-[10px] border border-ed-gold bg-ed-panel p-3 shadow-[0_6px_20px_rgba(0,0,0,0.08)]"
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.stopPropagation();
              setPending(null);
            }
          }}
          data-config-cross
        >
          <p className="text-[13px] font-semibold leading-[18px] text-ed-text" aria-live="polite">
            {words.title}
          </p>
          <p className="mt-0.5 text-[12px] leading-4 text-ed-text-2">{words.detail}</p>
          <div className="mt-2.5 flex gap-2">
            <button
              type="button"
              onClick={() => void confirm()}
              disabled={adding}
              className="inline-flex h-8 items-center rounded-lg bg-ed-ink px-3.5 text-[13px] font-semibold text-white hover:bg-black disabled:opacity-60"
              data-config-cross-add
            >
              {adding ? "Adding…" : "Add"}
            </button>
            <button
              type="button"
              onClick={() => setPending(null)}
              disabled={adding}
              className="inline-flex h-8 items-center rounded-lg border border-ed-line px-3.5 text-[13px] font-medium text-ed-text hover:bg-ed-hover"
              data-config-cross-cancel
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
      <MaterialCategoryGrid
        key={tab}
        categories={[tab]}
        subGroups
        hideIds={GEM_HIDDEN}
        labels={GEM_WORDS}
        target={{ apply: pick, hasTarget: true, activeId: activeId ?? null, custom: true }}
      />
    </div>
  );
}
